import {clsx} from 'clsx'
import type {ReactNode} from 'react'

import type {SelectedAgent} from '@github-ui/agent-sessions-types/agent-identity'
import {ConsecutiveCAPI401sProvider} from '@github-ui/copilot-shared-ui/capi/ConsecutiveCAPI401sContext'
import {ModelPicker} from './ModelPicker'
import {ReasoningEffortPicker} from './ReasoningEffortPicker'
import {AgentSelector} from './AgentSelector'
import {AgentSelectorIconTrigger} from './AgentSelectorMenuTrigger'
import {ImageAttachButton} from './ImageAttachButton'
import {McpPermissionModeSelector} from './McpPermissionModeSelector'
import type {TaskComposerController} from './use-task-composer'
import {isCustomAgentPickerAvailable} from './use-environment-selection'
import styles from './ComposerToolbar.module.css'
import responsiveStyles from '../shared/responsive-composer.module.css'

/**
 * The toolbar rendered inside the task input's toolbar slot (`toolbarContent` for the composer,
 * `extraToolbar` for the legacy `ChatInput`). Everything here is identical between the two input
 * primitives, so it is driven entirely by the shared {@link TaskComposerController}.
 */
export function ComposerToolbar({
  controller,
  leadingControl,
  modeControl,
  responsiveIconOnly,
}: {
  controller: TaskComposerController
  leadingControl?: ReactNode
  /** Replaces the legacy MCP permission selector when a host owns the mode controls. */
  modeControl?: ReactNode
  /** Opts the pickers into the composer's container-query compaction. */
  responsiveIconOnly?: boolean
}) {
  const {
    isAhpEnvironment,
    isSandboxDraft,
    selectedRepo,
    selectedBranch,
    isMcpPermissionGateEnabled,
    mcpPermissionMode,
    setMcpPermissionMode,
    textAreaRef,
    isAtImageLimit,
    isSubAgentsEnabled,
    selectedAgent,
    setSelectedAgent,
    shouldShowModelPicker,
    selectedModelId,
    selectModel,
    isModelSelectionReady,
    vendorFilters,
    isTokenBasedBillingEnabled,
    isReasoningEffortEnabled,
    selectedReasoningEffort,
    setSelectedReasoningEffort,
    supportedReasoningEfforts,
  } = controller
  const agentSelectorRepositoryProps = selectedRepo
    ? {repo: selectedRepo, branch: selectedBranch || selectedRepo.defaultBranch}
    : {repo: undefined, branch: undefined}
  const isAgentPickerAvailable = isCustomAgentPickerAvailable({
    isSubAgentsEnabled,
    hasSelectedRepository: Boolean(selectedRepo),
  })

  return (
    <div className={clsx(styles.extraToolbarActions, responsiveIconOnly && responsiveStyles.pickers)}>
      {!isSandboxDraft && (
        <ImageAttachButton
          textAreaRef={textAreaRef}
          repositoryId={selectedRepo?.id?.toString()}
          disabled={!selectedRepo}
          isAtImageLimit={isAtImageLimit}
          size="medium"
        />
      )}
      {leadingControl}
      {modeControl !== undefined
        ? modeControl
        : (!isAhpEnvironment || isSandboxDraft) &&
          isMcpPermissionGateEnabled && (
            <McpPermissionModeSelector
              selectedMode={mcpPermissionMode}
              onSelectMode={setMcpPermissionMode}
              forceIconOnly={false}
              hideLeadingVisual
              triggerClassName={styles.pickerButton}
            />
          )}
      {isAgentPickerAvailable && (
        // AgentSelector -> useSubAgents -> useCAPIQuery requires ConsecutiveCAPI401sProvider.
        // The strip toolbar renders AgentSelector directly, so we wrap it here. AgentsView
        // already provides one higher in the tree, but CopilotChatInputPartial does not, so
        // this wrap is load-bearing for those consumers.
        <ConsecutiveCAPI401sProvider>
          <AgentSelector
            {...agentSelectorRepositoryProps}
            repositoryOptional={isSandboxDraft}
            customAgentsOnly={isAhpEnvironment}
            selectedAgent={selectedAgent}
            onSelectAgent={(agent: SelectedAgent) => setSelectedAgent(agent)}
            iconOnly
            triggerComponent={AgentSelectorIconTrigger}
            triggerClassName={clsx(styles.pickerButton, !isMcpPermissionGateEnabled && styles.agentAfterAttach)}
          />
        </ConsecutiveCAPI401sProvider>
      )}
      {(!isModelSelectionReady || shouldShowModelPicker) && (
        <div className={styles.pickerButtonWrapper}>
          <ModelPicker
            selectedModelId={selectedModelId}
            onUpdateModel={selectModel}
            vendorFilters={vendorFilters}
            isTokenBasedBillingEnabled={isTokenBasedBillingEnabled}
            showTriggerIcon={false}
            showLoadingState={!isModelSelectionReady}
            size="medium"
            responsiveIconOnly={responsiveIconOnly}
          />
        </div>
      )}
      {isModelSelectionReady && isReasoningEffortEnabled && (
        <ReasoningEffortPicker
          selectedReasoningEffort={selectedReasoningEffort}
          onUpdateReasoningEffort={setSelectedReasoningEffort}
          supportedReasoningEfforts={supportedReasoningEfforts}
          triggerClassName={styles.pickerButton}
          responsiveIconOnly={responsiveIconOnly}
        />
      )}
    </div>
  )
}
